import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import {
  addSourceSecret,
  createSourceAuthMethod,
  deleteSourceAuthMethod,
  updateSource,
  updateSourceAuthMethod,
  type TSourceAuthMethod,
} from '../../entities/source';

export interface IStagedSender {
  presetId: string;
  versionId: string;
  name: string;
  secret?: string;
}

const invalidate = async (
  queryClient: ReturnType<typeof useQueryClient>,
  sourceId: string,
): Promise<void> => {
  // The scheme is read through the preset list, matched by key, so both must refetch before it can be named.
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['source-auth-methods', sourceId] }),
    queryClient.invalidateQueries({ queryKey: ['auth-presets'] }),
    queryClient.invalidateQueries({ queryKey: ['source', sourceId] }),
    queryClient.invalidateQueries({ queryKey: ['source-secrets', sourceId] }),
  ]);
};

export const useBindSender = (
  sourceId: string,
  hmacMethodId: string | null,
  onBound: () => void,
): UseMutationResult<TSourceAuthMethod, Error, IStagedSender> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sender: IStagedSender) => {
      // PATCH, never delete-and-recreate: secrets hang off the method id and would go with it.
      const method = hmacMethodId
        ? await updateSourceAuthMethod(sourceId, hmacMethodId, {
            authPresetVersionId: sender.versionId,
          })
        : await createSourceAuthMethod(sourceId, {
            method: 'hmac',
            enabled: true,
            useProviderRanges: false,
            authPresetVersionId: sender.versionId,
          });

      if (sender.secret) await addSourceSecret(sourceId, method.id, sender.secret);
      return method;
    },
    onSuccess: async () => {
      await invalidate(queryClient, sourceId);
      onBound();
    },
  });
};

// `all`, not the default `any`: under `any` a valid signature from anywhere still passes, so the list would filter nothing.
export const useRequireAddresses = (
  sourceId: string,
  ipFilter: TSourceAuthMethod | null,
): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (providerRangesKey: string) => {
      if (ipFilter) {
        await updateSourceAuthMethod(sourceId, ipFilter.id, {
          enabled: true,
          useProviderRanges: true,
          providerRangesKey,
        });
      } else {
        await createSourceAuthMethod(sourceId, {
          method: 'ip_allowlist',
          enabled: true,
          useProviderRanges: true,
          providerRangesKey,
        });
      }
      await updateSource(sourceId, { authCombine: 'all' });
    },
    onSuccess: async () => {
      await invalidate(queryClient, sourceId);
      await queryClient.invalidateQueries({ queryKey: ['sources'] });
    },
  });
};

export const useRemoveCheck = (
  sourceId: string,
  onRemoved: () => void,
): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (methodId: string) => deleteSourceAuthMethod(sourceId, methodId),
    onSuccess: async () => {
      await invalidate(queryClient, sourceId);
      onRemoved();
    },
  });
};
